import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@ipreo/northstar';
import { combineLatest, Observable, of } from 'rxjs';

import {
  DealAnalytics,
  OASMaturities,
  MaturityCouponOverrides
} from '../types/analytics.types';
import { isBuyside } from '../utils/platform';
import { select, Store } from '@ngrx/store';
import { Deal } from '../types/deal.types';
import {
  selectBids,
  selectBidsLoadingState,
  selectBidsLoadedState,
  SharedState
} from '../state/reducer';
import {
  concatMap,
  distinctUntilChanged,
  map,
  withLatestFrom
} from 'rxjs/operators';

@Injectable()
export class AnalyticsService {
  public env: string;
  public muniApiHost: string;
  private readonly bids$: Observable<Deal[]> = this.store.pipe(
    select(selectBids)
  );

  private readonly bidsLoadingState$: Observable<boolean> = this.store.pipe(
    select(selectBidsLoadingState)
  );

  private readonly bidsLoadedState$: Observable<boolean> = this.store.pipe(
    select(selectBidsLoadedState)
  );

  private readonly haveBidsLoaded$: Observable<boolean> = combineLatest(
    this.bidsLoadingState$,
    this.bidsLoadedState$
  ).pipe(map(([loading, loaded]: [boolean, boolean]) => !loading && loaded));

  constructor(
    public http: HttpClient,
    @Inject(ENVIRONMENT_CONFIG) private config: EnvironmentConfig,
    private readonly store: Store<SharedState>
  ) {
    this.env = AnalyticsService.getEnvironment(config);
    this.muniApiHost = config.params.muniApiHost as string;
  }

  private static getEnvironment(config: EnvironmentConfig): string {
    return isBuyside(config)
      ? (config.params.buySideEnv as string)
      : (config.params.env as string);
  }

  public getAnalytics(
    id: string,
    overrides: MaturityCouponOverrides
  ): Observable<DealAnalytics> {
    const url = `${this.muniApiHost}/analytics/${this.env}/${id}`;
    return this.http.post<DealAnalytics>(
      url,
      { maturities: [], overrides: overrides },
      { headers: this.getHeaders() }
    );
  }
  public getBidAnalytics(
    id: string,
    overrides: MaturityCouponOverrides
  ): Observable<DealAnalytics> {
    return this.haveBidsLoaded$.pipe(
      withLatestFrom(this.bids$),
      map(([haveBidsLoaded, bids]) => {
        if (haveBidsLoaded) {
          return this.composeBidsForOAS(bids);
        } else {
          return [];
        }
      }),
      distinctUntilChanged((x, y) => JSON.stringify(x) === JSON.stringify(y)),
      // call analytics API once for all bids to get Duration & OAS
      concatMap((oasMaturities: OASMaturities[]) => {
        return this.fetchOASMaturities(oasMaturities, id, overrides);
      })
    );
  }

  public composeBidsForOAS(bids: Deal[]): OASMaturities[] {
    const oasMaturities: OASMaturities[] = [];
    bids.forEach((bid) => {
      const bidOas = bid?.published?.maturities.reduce((prev, curr) => {
        const { id, coupon, numberBonds, price, maturityDate, seriesId } = curr;
        if (id && coupon && numberBonds && price && maturityDate && seriesId) {
          const i_date = bid.published.series
            .find((serie) => serie.id == seriesId)
            .deliveryDate.format('YYYY-MM-DD');

          const oasInput = {
            id,
            coupon,
            price,
            i_date,
            face_amount: numberBonds * 1000,
            cusip: '',
            m_date: maturityDate.format('YYYY-MM-DD'),
            name: id,
            calls: [],
            sinking_funds: []
          };
          if (curr.callFeature) {
            if (
              curr.callFeature.firstCallDate &&
              curr.callFeature.firstCallPrice
            ) {
              oasInput.calls.push({
                i_date: curr.callFeature.firstCallDate.format('YYYY-MM-DD'),
                price: curr.callFeature.firstCallPrice
              });
            }
            if (
              curr.callFeature.lastCallDate &&
              curr.callFeature.lastCallPrice
            ) {
              oasInput.calls.push({
                i_date: curr.callFeature.lastCallDate.format('YYYY-MM-DD'),
                price: curr.callFeature.lastCallPrice
              });
            }
          }
          prev.push(oasInput);
        }
        return prev;
      }, []);

      oasMaturities.push(...bidOas);
    });

    return oasMaturities;
  }

  public fetchOASMaturities(
    oasMaturities: OASMaturities[],
    id: string,
    overrides: MaturityCouponOverrides
  ) {
    const url = `${this.muniApiHost}/analytics/${this.env}/${id}`;
    if (oasMaturities) {
      return this.http
        .post<DealAnalytics>(
          url,
          {
            maturities: oasMaturities,
            overrides: overrides
          },
          { headers: this.getHeaders(), observe: 'response' }
        )
        .pipe(map((res) => (res ? (res.body as any) : null)));
    } else {
      return of({ maturities: [] });
    }
  }

  private getHeaders(): { [key: string]: string } {
    const headers = {};
    if (
      isBuyside(this.config) &&
      !this.config['northstar'].host.api.includes('polaris')
    ) {
      headers['Authorization'] = 'BSP-JWT';
    }
    return headers;
  }
}
